import type { KeybindingCommandId, KeybindingNotice, KeybindingNoticeKind } from '../model/keybindingTypes';
import type { KeybindingsData } from './keybindingsStoreTypes';

/**
 * Plain derivations over store state for the Settings tab. The ones returning
 * arrays build a new array per call — wrap them in `useShallow` when used as a
 * zustand hook selector.
 */

/** True when the command has its own override (bound, rebound or unbound). */
export function isCustomised(state: Pick<KeybindingsData, 'overrides'>, commandId: KeybindingCommandId): boolean {
  return Object.prototype.hasOwnProperty.call(state.overrides, commandId);
}

/** Notices about one command's bindings (warnings and displacement rows). */
export function noticesFor(
  state: Pick<KeybindingsData, 'notices'>,
  commandId: KeybindingCommandId,
): KeybindingNotice[] {
  return state.notices.filter((notice) => notice.commandId === commandId);
}

/** Durable changes validation wrote (displaced losers, refused reserved chords). */
export function durableNotices(state: Pick<KeybindingsData, 'notices'>): KeybindingNotice[] {
  return state.notices.filter((notice) => notice.persisted);
}

const UNRECLAIMABLE: ReadonlySet<KeybindingNoticeKind> = new Set<KeybindingNoticeKind>(['layout-collision', 'reserved', 'duplicate']);

/**
 * An inactive chord that recording it again can make win: it lost a conflict
 * with another command's shortcut. One suppressed by the keyboard layout, a
 * platform reserve, or as a duplicate of its own row cannot be reclaimed.
 */
export function canReclaim(
  state: Pick<KeybindingsData, 'keymap' | 'notices'>,
  commandId: KeybindingCommandId,
  chord: string,
): boolean {
  if (state.keymap[commandId].includes(chord)) return false;
  return !state.notices.some((notice) =>
    notice.commandId === commandId && notice.chord === chord && UNRECLAIMABLE.has(notice.kind));
}

/** Undo is offered only for a durable notice made by the change the current snapshot reverses (J2). */
export function canUndoNotice(state: Pick<KeybindingsData, 'undoSnapshot'>, notice: KeybindingNotice): boolean {
  return state.undoSnapshot !== null && notice.origin === 'mutation';
}
